import React, { useState, useEffect } from 'react';
import { 
  ThumbsUp, Reply, Shield, Send
} from 'lucide-react';
import { issuesApi } from '../../services/issuesApi';

export default function CommentSection({ issueId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');
  const [replyTo, setReplyTo] = useState(null);
  const [helpfulMarked, setHelpfulMarked] = useState(new Set());
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    commentText: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    loadComments();
  }, [issueId, sortBy]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const data = await issuesApi.getComments(issueId, sortBy);
      setComments(data.comments || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.commentText) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      await issuesApi.addComment(issueId, formData);
      setFormData({ name: '', email: '', commentText: '' });
      await loadComments();
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to submit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplySubmit = async (parentCommentId) => {
    if (!replyText.trim()) return;
    
    try {
      setSubmitting(true);
      await issuesApi.addComment(issueId, {
        name: formData.name || 'Anonymous',
        email: formData.email || 'anonymous@user.com',
        commentText: replyText,
        parentCommentId
      });
      setReplyTo(null);
      setReplyText('');
      await loadComments();
    } catch (error) {
      console.error('Error submitting reply:', error);
      alert('Failed to submit reply. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkHelpful = async (commentId) => {
    if (helpfulMarked.has(commentId)) return;
    
    try {
      await issuesApi.markCommentHelpful(issueId, commentId);
      setHelpfulMarked(prev => new Set([...prev, commentId]));
      await loadComments();
    } catch (error) {
      if (error.response?.status === 400) {
        setHelpfulMarked(prev => new Set([...prev, commentId]));
      }
      console.error('Error marking helpful:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const CommentCard = ({ comment, isReply = false }) => (
    <div className={`comment-card ${comment.isAdminComment ? 'admin-comment' : ''}`}>
      <div className="comment-header">
        <div className="comment-author">
          {comment.isAdminComment && (
            <span className="admin-badge">
              <Shield size={10} />
              Staff
            </span>
          )}
          {comment.name}
        </div>
        <div className="comment-date">{formatDate(comment.timestamp)}</div>
      </div>
      
      <div className="comment-text">{comment.commentText}</div>
      
      {comment.imageURLs && comment.imageURLs.length > 0 && (
        <div className="comment-images">
          {comment.imageURLs.map((url, idx) => (
            <img key={idx} src={url} alt="Comment attachment" />
          ))}
        </div>
      )}
      
      <div className="comment-actions">
        {!isReply && (
          <button 
            className="comment-action-btn"
            onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
          >
            <Reply size={14} />
            Reply
          </button>
        )}
        <button 
          className={`comment-action-btn helpful ${helpfulMarked.has(comment.id) ? 'marked' : ''}`}
          onClick={() => handleMarkHelpful(comment.id)}
          disabled={helpfulMarked.has(comment.id)}
        >
          <ThumbsUp size={14} />
          Helpful ({comment.helpfulCount || 0})
        </button>
      </div>
      
      {/* Reply Form */}
      {replyTo === comment.id && (
        <div className="reply-form">
          <textarea
            placeholder="Write your reply..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
          />
          <div className="reply-form-actions">
            <button 
              className="btn-secondary"
              onClick={() => {
                setReplyTo(null);
                setReplyText('');
              }}
            >
              Cancel
            </button>
            <button 
              className="btn-primary"
              onClick={() => handleReplySubmit(comment.id)}
              disabled={submitting || !replyText.trim()}
            >
              Reply
            </button>
          </div>
        </div>
      )}
      
      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="comment-replies">
          {comment.replies.map(reply => (
            <CommentCard key={reply.id} comment={reply} isReply />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="comments-section">
      <div className="comments-header">
        <h3>Comments ({comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0)})</h3>
        <div className="comments-sort">
          <label>Sort:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="helpful">Most Helpful</option>
          </select>
        </div>
      </div>

      {/* Comment Form */}
      <form className="comment-form" onSubmit={handleSubmit}>
        <h4>Add a Comment</h4>
        <div className="comment-form-row">
          <input
            type="text"
            className="comment-form-input"
            placeholder="Your name *"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            required
          />
          <input
            type="email"
            className="comment-form-input"
            placeholder="Your email *"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            required
          />
        </div>
        <textarea
          className="comment-form-textarea"
          placeholder="Share your thoughts, experience, or suggestions..."
          value={formData.commentText}
          onChange={(e) => setFormData(prev => ({ ...prev, commentText: e.target.value }))}
          required
        />
        <div className="comment-form-actions">
          <button 
            type="submit" 
            className="comment-submit-btn"
            disabled={submitting}
          >
            {submitting ? 'Posting...' : (
              <>
                <Send size={16} />
                Post Comment
              </>
            )}
          </button>
        </div>
      </form>

      {/* Comments List */}
      {loading ? (
        <div className="empty-state">
          <div className="loading-spinner-small" style={{ margin: '0 auto' }} />
          <p>Loading comments...</p>
        </div>
      ) : comments.length === 0 ? (
        <div className="empty-state">
          <p>No comments yet. Be the first to share your thoughts!</p>
        </div>
      ) : (
        <div className="comments-list">
          {comments.map(comment => (
            <CommentCard key={comment.id} comment={comment} />
          ))}
        </div>
      )}
    </div>
  );
}







